import {
  COMMENT_BODY_MAX_LENGTH,
  COMMENT_BODY_MIN_LENGTH,
  type CreateCommentPayload,
  type CreateThreadPayload,
  type UpdateCommentPayload,
} from '@atlas/api-contract';
import { z } from 'zod';

import type { PendingPin } from '@/features/viewer/viewer-store';

/**
 * The one field every composer in this feature writes.
 *
 * The bounds are the shared contract's, so this schema and the server's
 * `@Length(...)` on the comment DTO cannot drift. Trimming happens *before* the
 * length checks, which is what makes a comment of spaces fail as empty rather
 * than pass as five characters (spec R5.3).
 */
export const commentBodySchema = z
  .string()
  .trim()
  .min(COMMENT_BODY_MIN_LENGTH, 'Write something before posting.')
  .max(
    COMMENT_BODY_MAX_LENGTH,
    `Use ${String(COMMENT_BODY_MAX_LENGTH)} characters or fewer.`,
  );

export const commentFormSchema = z.object({ body: commentBodySchema });

export type CommentFormValues = z.infer<typeof commentFormSchema>;

/** An empty composer, so every one of them opens clean. */
export const EMPTY_COMMENT_FORM: CommentFormValues = { body: '' };

/**
 * The anchor the agent captured, plus the first thing said about it.
 *
 * The pin candidate is spread wholesale rather than field by field: it is
 * `AtlasPinCandidateMessage` minus its discriminant, and `CreateThreadPayload`
 * is the same `ThreadAnchor` plus a body, so restating the twelve anchor fields
 * here would only create somewhere for them to drift. `viewportX`/`viewportY`
 * are dropped — they say where to float the composer on *this* screen at *this*
 * scroll offset, and mean nothing once the thread exists.
 */
export const toCreateThreadPayload = (
  pin: PendingPin,
  body: string,
): CreateThreadPayload => {
  const { viewportX: _viewportX, viewportY: _viewportY, ...anchor } = pin;

  return { ...anchor, body: body.trim() };
};

export const toCreateCommentPayload = (
  body: string,
): CreateCommentPayload => ({ body: body.trim() });

export const toUpdateCommentPayload = (body: string): UpdateCommentPayload => ({
  body: body.trim(),
});
